// src/components/bot/geminiService.js
// ─────────────────────────────────────────────────────────────────────────────
// Layer 7 — Gemini Service
//
// Responsibilities:
//   • Fetch AI settings (key + system prompt) from /settings/prompt-employee
//     exactly once per page session, cache at module level.
//   • Expose planRequest()   — fast planning call (gemini-2.5-flash, temperature=0)
//   • Expose generateAnswer() — full conversational response
//
// Uses the Gemini REST API via fetch() — no SDK, no extra npm package.
// ─────────────────────────────────────────────────────────────────────────────
import { botFetch } from './botApi';

const GEMINI_BASE  = 'https://generativelanguage.googleapis.com/v1beta/models';
const ANSWER_MODEL = 'gemini-2.5-flash';   // full model for natural answers
const PLAN_MODEL   = 'gemini-2.5-flash';    // fast model for JSON routing

const DEBUG_AI = true;

// Module-level session cache — survives React re-renders, resets on page reload.
let _settings = null; // { key: string, prompt: string }

// ── Settings ──────────────────────────────────────────────────────────────────

/**
 * Ensure AI settings are loaded. Returns cached value on subsequent calls.
 * Throws if the backend endpoint fails (so callers can display an error).
 */
export async function ensureSettings() {
  if (_settings) return _settings;

  const r = await botFetch('/settings/prompt-employee');
  if (!r.ok) {
    throw new Error(`Failed to load AI settings: ${r.message}`);
  }

  const { key, prompt } = r.data || {};
  if (!key) throw new Error('AI settings response is missing the API key.');

  _settings = { key: key.trim(), prompt: (prompt || '').trim() };
  return _settings;
}

/** Reset cached settings (e.g. for testing). Not called in normal usage. */
export function resetSettings() {
  _settings = null;
}

// ── Internal Gemini caller ────────────────────────────────────────────────────

const delay = (ms) => new Promise(res => setTimeout(res, ms));

/**
 * POST to the Gemini generateContent REST endpoint with exponential backoff.
 *
 * @param {string} model  - Model name (without base URL)
 * @param {string} key    - Gemini API key
 * @param {object} body   - Full request body
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<string>} - The text from the first candidate
 */
async function callGemini(model, key, body, maxRetries = 3) {
  const url = `${GEMINI_BASE}/${model}:generateContent?key=${key}`;
  const payloadStr = JSON.stringify(body);
  
  if (DEBUG_AI) {
    const estTokens = Math.ceil(payloadStr.length / 4);
    console.debug(`[Gemini Request Started] Model: ${model} | Approx Prompt Tokens: ${estTokens} | Payload Chars: ${payloadStr.length}`);
  }

  let attempt = 0;
  let baseDelay = 1000;

  while (attempt <= maxRetries) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadStr,
      });

      if (!res.ok) {
        // Handle rate limits and server errors
        if (res.status === 429 || res.status >= 500) {
          if (attempt === maxRetries) {
            const errText = await res.text().catch(() => 'unknown error');
            throw new Error(`Gemini API error (HTTP ${res.status}): ${errText.slice(0, 200)}`);
          }

          // Respect Retry-After if provided
          const retryAfter = res.headers.get('Retry-After');
          const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : baseDelay;
          
          if (DEBUG_AI) {
            console.warn(`[Gemini Warning] HTTP ${res.status}. Retrying in ${waitMs}ms (Attempt ${attempt + 1}/${maxRetries})`);
          }

          await delay(waitMs);
          baseDelay *= 2; // Exponential backoff
          attempt++;
          continue;
        }

        // For 400, 403, 404, do not retry
        const errText = await res.text().catch(() => 'unknown error');
        throw new Error(`Gemini API error (HTTP ${res.status}): ${errText.slice(0, 200)}`);
      }

      const data = await res.json();
      
      if (DEBUG_AI) {
        const outTokens = data?.usageMetadata?.candidatesTokenCount || 0;
        console.debug(`[Gemini Request Finished] Generation Tokens: ${outTokens}`);
      }

      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!text && text !== '') {
        const finishReason = data?.candidates?.[0]?.finishReason;
        throw new Error(`Gemini returned no content. Finish reason: ${finishReason || 'unknown'}`);
      }

      return text.trim();
      
    } catch (error) {
      // Catch network errors (TypeError from fetch)
      if (error.name === 'TypeError' || error.message.includes('fetch')) {
        if (attempt === maxRetries) throw error;
        if (DEBUG_AI) console.warn(`[Gemini Warning] Network error: ${error.message}. Retrying in ${baseDelay}ms...`);
        await delay(baseDelay);
        baseDelay *= 2;
        attempt++;
        continue;
      }
      throw error; // Re-throw other errors (e.g. 400 Bad Request)
    }
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Planning call: ask Gemini to decide whether the user's message requires a
 * backend API call, and if so, which endpoint.
 *
 * Uses the fast gemini-2.5-flash model with temperature=0 for deterministic JSON output.
 * Called by aiPlanner.js — not intended for direct use.
 *
 * @param {string} userMessage    - The raw user input
 * @param {string} catalogText    - Flat newline-separated list of available endpoints
 * @param {Array}  geminiHistory  - Previous turns in Gemini contents[] format
 * @returns {Promise<string>}     - Raw JSON string from Gemini
 */
export async function planRequest(userMessage, catalogText, geminiHistory = []) {
  const { key } = await ensureSettings();

  const planningPrompt = [
    'You are an API router for the VAHD veterinary management system.',
    '',
    'Available backend endpoints:',
    catalogText,
    '',
    `User asked: "${userMessage}"`,
    '',
    'Rules:',
    '- If the question requires live application data (attendance, employees, cases,',
    '  vaccinations, deworming, drugs, indents, stock, farmers, IoT sensors, reports,',
    '  dashboard KPIs, allocations, purchase orders, sales, leaves, sync status, etc.),',
    '  respond with needs_api=true and pick the BEST matching endpoint.',
    '- If the user ONLY provides a location name without a specific intent (e.g., "Karimnagar", "in Warangal"),',
    '  respond with needs_options=true and extract the location name.',
    '- If the question is a greeting, general help, explanation, or general veterinary',
    '  knowledge that does not need live data, respond with needs_api=false.',
    '- The "params" object should contain any query parameters the endpoint needs',
    '  (e.g. district name, date, search term). Use empty {} if none are needed.',
    '',
    'Respond ONLY with valid JSON — no markdown, no code fences, no explanation.',
    '',
    'Format when data is needed:',
    '{"needs_api":true,"endpoint":"<exact endpoint path>","params":{},"reason":"<short reason>"}',
    '',
    'Format when only a location is provided (ambiguous intent):',
    '{"needs_api":false,"needs_options":true,"location":"<Capitalized Location Name>"}',
    '',
    'Format when no data is needed:',
    '{"needs_api":false, "answer": "<your conversational answer here>"}',
  ].join('\n');

  const contents = [
    ...(geminiHistory || []),
    { role: 'user', parts: [{ text: planningPrompt }] }
  ];

  const body = {
    contents,
    generationConfig: {
      temperature: 0,
      maxOutputTokens: 1024,
    },
  };

  return callGemini(PLAN_MODEL, key, body);
}

/**
 * Generate the final conversational answer for the user.
 *
 * @param {Array}   geminiHistory - Previous turns in Gemini contents[] format
 * @param {string}  userMessage   - Current user message
 * @param {string|null} contextBlock - Serialized API response to inject, or null
 * @returns {Promise<string>}     - Natural language answer
 */
export async function generateAnswer(geminiHistory, userMessage, contextBlock) {
  const { key, prompt } = await ensureSettings();

  // Inject the API data into the user message so Gemini can reference it
  const enrichedUserText = contextBlock
    ? `${userMessage}\n\n[Live data retrieved from VAHD backend]\n${contextBlock}`
    : userMessage;

  const contents = [
    ...geminiHistory,
    { role: 'user', parts: [{ text: enrichedUserText }] },
  ];

  const body = {
    // system_instruction applies to every turn and sets the bot's persona/rules
    ...(prompt && {
      system_instruction: { parts: [{ text: prompt }] },
    }),
    contents,
    generationConfig: {
      temperature: 1.0,
      maxOutputTokens: 1024,
    },
  };

  return callGemini(ANSWER_MODEL, key, body);
}
